import Analytics from '@/utils/analytics';
import CloseIcon from '@assets/icons/close.svg';
import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useCallback, useState } from 'react';
import { BORDER_RADIUS_MEDIUM, FONTS, TYPOGRAPHY } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { PromotionErrorCode } from '@/enums';
import { Text, TouchableOpacity, View } from 'react-native';
import { TEXT_INPUT_HEIGHT, TextInput } from '@/components/text-input';
import { useApplyPromotions } from '@/hooks/use-apply-promotions';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigFlag,
} from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';

const DISCOUNT_CODE_MAX_LENGTH = 20;
const DISCOUNT_CODE_PATTERN = /^[A-Za-z0-9]+$/;
const PILL_REMOVE_HIT_SLOP = { bottom: 8, left: 8, right: 8, top: 8 };

interface Props {
  appliedCodes: string[];
  draftOrderUUID: string;
  onCodesApplied: () => Promise<void> | void;
}

export const DiscountCodeInput: React.FC<Props> = ({
  appliedCodes,
  draftOrderUUID,
  onCodesApplied,
}) => {
  const [styles, theme] = useStyles();
  const orderSummarySectionsEnabled = useDynamicConfigValue(
    'order_summary_sections_enabled',
  );
  const discountCodesEnabled = useSupplierDynamicConfigFlag(
    'promotions_config',
    'discount_codes_enabled',
    true,
  );

  const [applyPromotions] = useApplyPromotions();
  const { showErrorToast } = useToasts();
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const trimmedInput = input.trim();
  const canApply = trimmedInput.length > 0 && !isPending;
  const showExpanded = editing || appliedCodes.length > 0;

  const handleExpand = useCallback(() => {
    Analytics.track('order_summary.discount_code_button.press', {
      draftOrderUUID,
    });
    setEditing(true);
  }, [draftOrderUUID]);

  const handleInputChange = useCallback((value: string) => {
    setInput(value.slice(0, DISCOUNT_CODE_MAX_LENGTH));
    setError(null);
  }, []);

  const handleApply = useCallback(async () => {
    if (!canApply) {
      return;
    }
    if (!DISCOUNT_CODE_PATTERN.test(trimmedInput)) {
      setError(i18n.t('discount_code_invalid_characters'));
      Analytics.track('order_summary.discount_code.apply_failed', {
        discountCode: trimmedInput,
        draftOrderUUID,
      });
      return;
    }
    const isDuplicate = appliedCodes.some(
      (code) => code.toLowerCase() === trimmedInput.toLowerCase(),
    );
    if (isDuplicate) {
      setError(i18n.t('discount_code_already_applied'));
      Analytics.track('order_summary.discount_code.apply_failed', {
        discountCode: trimmedInput,
        draftOrderUUID,
      });
      return;
    }
    setIsPending(true);
    try {
      const { errorCode, success } = await applyPromotions({
        discountCodes: [...appliedCodes, trimmedInput],
        draftOrderUUID,
      });
      if (!success) {
        if (errorCode === PromotionErrorCode.MISSING_DISCOUNT_CODES) {
          setError(i18n.t('discount_code_invalid'));
        } else if (errorCode) {
          showErrorToast();
        }
        Analytics.track('order_summary.discount_code.apply_failed', {
          discountCode: trimmedInput,
          draftOrderUUID,
          errorCode,
        });
        return;
      }
      Analytics.track('order_summary.discount_code.apply', {
        discountCode: trimmedInput,
        draftOrderUUID,
      });
      await onCodesApplied();
      setInput('');
      setError(null);
    } finally {
      setIsPending(false);
    }
  }, [
    appliedCodes,
    applyPromotions,
    canApply,
    draftOrderUUID,
    onCodesApplied,
    showErrorToast,
    trimmedInput,
  ]);

  const handleRemove = useCallback(
    async (code: string) => {
      setIsPending(true);
      try {
        const { errorCode, success } = await applyPromotions({
          discountCodes: appliedCodes.filter((c) => c !== code),
          draftOrderUUID,
        });
        if (!success) {
          if (errorCode) {
            showErrorToast();
          }
          Analytics.track('order_summary.discount_code.remove_failed', {
            discountCode: code,
            draftOrderUUID,
            errorCode,
          });
          return;
        }
        Analytics.track('order_summary.discount_code.remove', {
          discountCode: code,
          draftOrderUUID,
        });
        await onCodesApplied();
      } finally {
        setIsPending(false);
      }
    },
    [
      appliedCodes,
      applyPromotions,
      draftOrderUUID,
      onCodesApplied,
      showErrorToast,
    ],
  );

  if (!discountCodesEnabled) {
    return null;
  }

  return (
    <View
      style={[
        styles.base,
        orderSummarySectionsEnabled ? styles.baseSections : null,
      ]}
    >
      {showExpanded ? (
        <>
          <Text style={[TYPOGRAPHY.small, FONTS.primarySemiBold, styles.label]}>
            {i18n.t('discount_code')}
          </Text>
          <View style={styles.inputRow}>
            <View style={styles.inputColumn}>
              <TextInput
                autoCorrect={false}
                autoFocus
                containerStyle={styles.inputContainer}
                editable={!isPending}
                error={error}
                onBlur={() => {
                  if (!trimmedInput && appliedCodes.length === 0 && !error) {
                    setEditing(false);
                  }
                }}
                onChangeText={handleInputChange}
                onSubmitEditing={handleApply}
                placeholder={i18n.t('discount_code_placeholder')}
                returnKeyType="done"
                style={TYPOGRAPHY.medium}
                testID="discount-code-input"
                value={input}
              />
            </View>
            <Button
              containerStyle={styles.applyButtonContainer}
              disabled={!canApply}
              loading={isPending}
              onPress={handleApply}
              size="medium"
              style={styles.applyButton}
              trackingData={null}
              type="primary"
            >
              {i18n.t('apply')}
            </Button>
          </View>
          {appliedCodes.length > 0 ? (
            <View style={styles.pillRow}>
              {appliedCodes.map((code) => (
                <View
                  key={code}
                  style={styles.pill}
                  testID={`discount-code-pill-${code}`}
                >
                  <Text style={[TYPOGRAPHY.listSubtitle, styles.pillText]}>
                    {code}
                  </Text>
                  <TouchableOpacity
                    accessibilityLabel={i18n.t(
                      'discount_code_remove_button_label',
                    )}
                    disabled={isPending}
                    hitSlop={PILL_REMOVE_HIT_SLOP}
                    onPress={() => handleRemove(code)}
                    testID={`discount-code-pill-${code}-remove`}
                  >
                    <CloseIcon
                      height={10}
                      stroke={theme.colors.primary}
                      width={10}
                    />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          ) : null}
        </>
      ) : (
        <TouchableOpacity
          onPress={handleExpand}
          style={styles.collapsedButton}
          testID="discount-code-collapsed-button"
        >
          <Text style={TYPOGRAPHY.pSemiBold}>{i18n.t('discount_code')}</Text>
          <PlusIcon fill={theme.colors.primary} height={24} width={24} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  applyButton: {
    height: TEXT_INPUT_HEIGHT,
  },
  applyButtonContainer: {
    marginLeft: theme.sk.spacing.spaceSmall,
  },
  base: {
    backgroundColor: theme.colors.white,
    borderBottomColor: theme.colors.background,
    borderBottomWidth: 2,
  },
  baseSections: {
    backgroundColor: theme.colors.white,
    borderBottomWidth: 0,
    borderRadius: BORDER_RADIUS_MEDIUM,
    marginBottom: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  collapsedButton: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  inputColumn: {
    flex: 1,
  },
  inputContainer: {
    borderColor: theme.colors.inactiveFill,
  },
  inputRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    paddingBottom: theme.sk.spacing.spaceSMedium,
  },
  label: {
    paddingBottom: theme.sk.spacing.spaceXsmall,
    paddingTop: theme.sk.spacing.spaceSMedium,
  },
  pill: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    paddingLeft: theme.sk.spacing.spaceSmall,
    paddingRight: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceSMedium,
  },
  pillText: {
    color: theme.colors.primary,
    lineHeight: 16,
  },
}));
